<template>
  <div class="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2 font-sans">
    <!-- Expanded Panel -->
    <transition name="slide-up">
      <div
        v-if="isOpen"
        class="bg-white rounded-xl shadow-2xl w-[90vw] md:w-[380px] border border-orange-200 flex flex-col overflow-hidden"
      >
        <!-- Header -->
        <div class="bg-orange-500 text-white px-4 py-3 flex justify-between items-center">
          <div class="flex items-center gap-2">
            <i class="pi pi-shield text-lg"></i>
            <h3 class="font-bold text-sm">Permission Required</h3>
          </div>
          <Button
            icon="pi pi-times"
            text
            rounded
            class="text-white !p-0 !w-7 !h-7 hover:bg-orange-600"
            @click="close"
          />
        </div>

        <!-- Denial List -->
        <div class="flex-1 overflow-y-auto max-h-[300px] p-3 bg-orange-50/50">
          <p class="text-xs text-gray-500 mb-3">
            Some data on this page requires permissions you don't currently have.
          </p>

          <div class="flex flex-col gap-2">
            <div
              v-for="denial in store.uniqueDenials"
              :key="denial.permission"
              class="bg-white rounded-lg p-3 border border-orange-100 shadow-sm"
            >
              <div class="flex items-start gap-2">
                <i class="pi pi-lock text-orange-400 mt-0.5"></i>
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium text-gray-800">
                    {{ formatPermission(denial.permission) }}
                  </div>
                  <div class="text-xs text-gray-500 mt-0.5">
                    {{ denial.message }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer Actions -->
        <div class="p-3 border-t border-orange-100 bg-white">
          <Button
            label="Request Access"
            icon="pi pi-send"
            size="small"
            class="w-full"
            severity="warn"
            @click="goToRequestRoles"
          />
          <p class="text-[10px] text-gray-400 text-center mt-2">
            Your administrator will review the request.
          </p>
        </div>
      </div>
    </transition>

    <!-- Floating Badge Button -->
    <transition name="fade-in">
      <div v-if="store.hasDenials && !store.dismissed" class="relative">
        <Button
          icon="pi pi-shield"
          rounded
          size="large"
          severity="warn"
          class="w-12 h-12 shadow-xl !border-none"
          v-tooltip.left="'Permission issues detected'"
          @click="toggle"
        />
        <!-- Pulse badge -->
        <span
          class="absolute -top-1 -right-1 w-5 h-5 bg-red-500 border-2 border-white rounded-full flex items-center justify-center text-[10px] text-white font-bold"
        >
          {{ store.uniqueDenials.length }}
        </span>
        <span
          class="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full animate-ping opacity-50"
        ></span>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { usePermissionDenialStore } from '@/store/permissionDenialStore';

const store = usePermissionDenialStore();
const router = useRouter();
const route = useRoute();
const isOpen = ref(false);

// Expose to browser console: window.__permissionWidget.show() / .hide()
onMounted(() => {
  (window as unknown as Record<string, unknown>).__permissionWidget = {
    show: () => store.toggleForceShow(),
    test: (permission = 'students.view') => {
      store.addDenials([{
        permission,
        message: `You do not have the '${permission}' permission. Contact your administrator.`,
      }]);
    },
  };
});

onUnmounted(() => {
  delete (window as unknown as Record<string, unknown>).__permissionWidget;
});

watch(() => route.fullPath, () => {
  if (!store.forceShow) {
    store.clear();
    isOpen.value = false;
  }
});

function toggle() {
  isOpen.value = !isOpen.value;
}

function close() {
  isOpen.value = false;
  store.dismiss();
}

function formatPermission(key: string): string {
  return key
    .replace(/\./g, ' - ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function goToRequestRoles() {
  isOpen.value = false;
  store.dismiss();
  router.push({ name: 'admin-my-account' });
}
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
.fade-in-enter-active,
.fade-in-leave-active {
  transition: opacity 0.3s ease;
}
.fade-in-enter-from,
.fade-in-leave-to {
  opacity: 0;
}
</style>
